import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
} from '@mui/material'

import { AppDispatch, RootState } from '../../store'
import { getMenuSectionsForRole, getIconComponent } from '../../config/menuConfig'
import { fetchLeaveStats } from '../../store/slices/leaveSlice'
import { useSocket } from '../../contexts/SocketContext'
import CountBadge from '../../shared-components/CountBadge'

interface SidebarProps {
  open: boolean
  onToggle: () => void
  width: number
  isMobile: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, width, isMobile }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { projects } = useSelector((state: RootState) => state.projects)
  const { stats: leaveStats } = useSelector((state: RootState) => state.leaves)
  const { socket, isConnected } = useSocket()

  const isApprover = user?.role === 'admin' || user?.role === 'manager'
  const pendingLeaveCount = leaveStats?.pending ?? 0

  // Initial count so the badge is correct as soon as the sidebar mounts,
  // before any socket event has had a chance to arrive.
  useEffect(() => {
    if (isApprover) dispatch(fetchLeaveStats())
  }, [isApprover, dispatch])

  // Kept live via the same broadcasts the backend already sends to every
  // admin/manager - a fresh stats fetch (rather than manual increment/
  // decrement here) avoids drift across create/approve/reject/cancel.
  useEffect(() => {
    if (!socket || !isConnected || !isApprover) return
    const refreshLeaveStats = () => dispatch(fetchLeaveStats())
    socket.on('leave-created', refreshLeaveStats)
    socket.on('leave-queue-updated', refreshLeaveStats)
    return () => {
      socket.off('leave-created', refreshLeaveStats)
      socket.off('leave-queue-updated', refreshLeaveStats)
    }
  }, [socket, isConnected, isApprover, dispatch])

  // Get menu sections based on user role
  const baseMenuSections = user ? getMenuSectionsForRole(user.role) : []
  // Inject the live pending count into the Leaves item without mutating the
  // shared static menuConfig array (it's a module-level singleton).
  const menuSections = baseMenuSections.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.path === '/leaves' && pendingLeaveCount > 0
        ? { ...item, badge: pendingLeaveCount }
        : item
    ),
  }))

  const handleItemClick = (path: string, external?: boolean) => {
    if (external) {
      window.location.href = path
    } else {
      navigate(path)
    }
    if (isMobile) {
      onToggle()
    }
  }

     const isActive = (path: string) => {
     // Special handling for kanban routes - only highlight if we're on the main kanban page
     if (path === '/kanban') {
       return location.pathname === '/kanban' || location.pathname.includes('/projects/') && location.pathname.includes('/kanban')
     }
     // For other routes, don't highlight if we're on a kanban page
     if (location.pathname.includes('/kanban')) {
       return false
     }
     return location.pathname === path || location.pathname.startsWith(path + '/')
   }

  // Role color and display name are now handled by the role management utilities



  const drawerContent = (
    <Box sx={{ overflow: 'auto', height: '100%', backgroundColor: '#ffffff' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src="/LOGO-Icon.png"
            alt="SAR Vexa Logo"
            sx={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              border: '2px solid #3b82f6',
              borderRadius: '50%',
              padding: 0.4,
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', lineHeight: 1.2 }}>
              VEXA
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, lineHeight: 1.2 }}>
              Drive, challenge, progress
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Sections */}
      <Box sx={{ py: 1 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {/* Section Title */}
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 700, 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.65rem',
                  color: '#94a3b8',
                }}
              >
                {section.title}
              </Typography>
            </Box>

            {/* Section Items */}
            <List dense sx={{ py: 0, px: 0.75 }}>
              {section.items.map((item) => {
                const active = isActive(item.path)
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      selected={active}
                      onClick={() => handleItemClick(item.path, item.external)}
                      sx={{
                        mx: 0.5,
                        my: 0.2,
                        borderRadius: 1.5,
                        py: 0.6,
                        px: 1.25,
                        minHeight: 36,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f1f5f9',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#2563eb',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: active ? 'white' : '#64748b',
                          minWidth: 32,
                          '& svg': {
                            fontSize: '1.1rem',
                          }
                        }}
                      >
                        {(() => {
                          const IconComponent = getIconComponent(item.iconName)
                          return item.badge ? (
                            <CountBadge
                              count={item.badge}
                              sx={{
                                '& .MuiBadge-badge': {
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 16,
                                  minWidth: 16,
                                  fontWeight: 600,
                                }
                              }}
                            >
                              <IconComponent />
                            </CountBadge>
                          ) : (
                            <IconComponent />
                          )
                        })()}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { 
                            fontWeight: active ? 700 : 500,
                            fontSize: '0.8125rem',
                            color: active ? 'white' : '#1e293b',
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>

            {/* Section Divider */}
            {section.divider && sectionIndex < menuSections.length - 1 && (
              <Divider sx={{ my: 1, mx: 2, borderColor: '#e2e8f0' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Recent Projects Section */}
      {projects.length > 0 && (
        <>
          <Divider sx={{ mx: 2, my: 1, borderColor: '#e2e8f0' }} />
          <Box sx={{ px: 2, py: 0.75 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 700, 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.65rem',
                color: '#94a3b8',
              }}
            >
              Recent Projects
            </Typography>
          </Box>
          <List dense sx={{ py: 0, px: 0.75 }}>
            {projects.slice(0, 5).map((project) => {
              const projectNameSlug = project.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
              const projectPath = `/projects/${projectNameSlug}/kanban`
              const active = location.pathname.includes(projectNameSlug) || location.pathname.includes(project._id)
              const isOnKanbanPage = location.pathname.includes('/kanban')
              
              const handleProjectClick = () => {
                if (isOnKanbanPage) {
                  // If already on kanban page, use replace to avoid full navigation
                  // This makes it smooth like the dropdown
                  navigate(projectPath, { replace: true })
                } else {
                  // If not on kanban page, navigate normally
                  navigate(projectPath)
                }
                if (isMobile) {
                  onToggle()
                }
              }
              
              return (
                <ListItem key={project._id} disablePadding>
                  <ListItemButton
                    selected={active}
                    onClick={handleProjectClick}
                    sx={{
                      mx: 0.5,
                      my: 0.2,
                      borderRadius: 1.5,
                      py: 0.6,
                      px: 1.25,
                      minHeight: 36,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: active ? 'white' : '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: active ? '#3b82f6' : 'white',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          boxShadow: active ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.2)',
                        }}
                      >
                        {project.key}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={project.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        sx: {
                          fontWeight: active ? 700 : 500,
                          fontSize: '0.8125rem',
                          color: active ? 'white' : '#1e293b',
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        </>
      )}
    </Box>
  )

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onToggle}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          // Ensure sidebar starts from the top and goes full height
          top: 0,
          height: '100vh',
          // Ensure proper z-index
          zIndex: (theme) => theme.zIndex.drawer,
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar
